import { deflateSync, inflateSync } from "fflate";

// Rain products live on a Web Mercator grid centered on the radar so the
// result drops onto Leaflet as an exact L.imageOverlay. Row 0 is north.
export const CELL_M = 500;
export const RANGE_M = 230000;

const EARTH_R = 6378137;
const DEG = Math.PI / 180;
const MISSING_U16 = 0xffff;
const U16_SCALE = 100; // 0.01 mm/h

function mercY(lat) {
  return EARTH_R * Math.log(Math.tan(Math.PI / 4 + (lat * DEG) / 2));
}

function mercLat(y) {
  return (2 * Math.atan(Math.exp(y / EARTH_R)) - Math.PI / 2) / DEG;
}

export function createRainGrid({ lat, lon, rangeM = RANGE_M, cellM = CELL_M }) {
  // Mercator stretches poleward, so size the square for the scale at the
  // coverage edge nearest the pole or its far side gets clipped.
  const cellMerc = cellM / Math.cos(lat * DEG);
  const edgeLat = Math.min(89, Math.abs(lat) + rangeM / 111000);
  const n = 2 * Math.ceil(rangeM / Math.cos(edgeLat * DEG) / cellMerc);
  const cx = EARTH_R * lon * DEG;
  const cy = mercY(lat);
  const x0 = cx - (n / 2) * cellMerc;
  const y0 = cy + (n / 2) * cellMerc;
  const west = x0 / EARTH_R / DEG;
  const east = (x0 + n * cellMerc) / EARTH_R / DEG;
  return {
    lat,
    lon,
    cellM,
    cellMerc,
    nx: n,
    ny: n,
    x0,
    y0,
    bounds: [[mercLat(y0 - n * cellMerc), west], [mercLat(y0), east]],
  };
}

// Fractional (col, row) where integer values are cell centers.
export function latLonToCell(grid, lat, lon) {
  const x = EARTH_R * lon * DEG;
  const y = mercY(lat);
  return { col: (x - grid.x0) / grid.cellMerc - 0.5, row: (grid.y0 - y) / grid.cellMerc - 0.5 };
}

export function cellToLatLon(grid, col, row) {
  const x = grid.x0 + (col + 0.5) * grid.cellMerc;
  const y = grid.y0 - (row + 0.5) * grid.cellMerc;
  return { lat: mercLat(y), lon: x / EARTH_R / DEG };
}

function rangeBearing(lat1, lon1, lat2, lon2) {
  const p1 = lat1 * DEG;
  const p2 = lat2 * DEG;
  const dl = (lon2 - lon1) * DEG;
  const a = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  const range = 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(a)));
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  const bearing = ((Math.atan2(y, x) / DEG) + 360) % 360;
  return { range, bearing };
}

// 0.1° azimuth bins -> nearest radial. Level 3 radials report their start
// angle; Level 2 reports the center, so callers pass azimuthIsStart.
function buildAzimuthLookup(azimuths, beamWidth, azimuthIsStart) {
  const centers = Array.from(azimuths, (a) => (azimuthIsStart ? a + beamWidth / 2 : a) % 360);
  const lookup = new Int32Array(3600).fill(-1);
  for (let bin = 0; bin < 3600; bin++) {
    const angle = bin / 10 + 0.05;
    let best = -1;
    let bestDist = Infinity;
    for (let r = 0; r < centers.length; r++) {
      const d = Math.abs(((angle - centers[r] + 540) % 360) - 180);
      if (d < bestDist) {
        bestDist = d;
        best = r;
      }
    }
    if (bestDist <= beamWidth) lookup[bin] = best;
  }
  return lookup;
}

// Maps each grid cell to a flat polar index (radial * numGates + gate), or -1
// outside coverage. Nearest-neighbor; build once per station/geometry.
export function buildPolarLut(grid, { lat, lon, azimuths, numGates, rangeStartM, rangeStepM, beamWidth = 1, azimuthIsStart = true }) {
  const azLookup = buildAzimuthLookup(azimuths, beamWidth, azimuthIsStart);
  const lut = new Int32Array(grid.nx * grid.ny).fill(-1);
  const maxRange = rangeStartM + numGates * rangeStepM;
  for (let row = 0; row < grid.ny; row++) {
    for (let col = 0; col < grid.nx; col++) {
      const ll = cellToLatLon(grid, col, row);
      const { range, bearing } = rangeBearing(lat, lon, ll.lat, ll.lon);
      if (range < rangeStartM || range >= maxRange) continue;
      const radial = azLookup[Math.min(3599, Math.floor(bearing * 10))];
      if (radial < 0) continue;
      const gate = Math.floor((range - rangeStartM) / rangeStepM);
      lut[row * grid.nx + col] = radial * numGates + gate;
    }
  }
  return lut;
}

export function resamplePolar(values, lut, out = new Float32Array(lut.length)) {
  for (let i = 0; i < lut.length; i++) {
    const p = lut[i];
    out[i] = p >= 0 ? values[p] : NaN;
  }
  return out;
}

export function packRate(rate) {
  const u16 = new Uint16Array(rate.length);
  for (let i = 0; i < rate.length; i++) {
    const v = rate[i];
    u16[i] = Number.isFinite(v) ? Math.min(MISSING_U16 - 1, Math.round(v * U16_SCALE)) : MISSING_U16;
  }
  return deflateSync(new Uint8Array(u16.buffer), { level: 1 });
}

export function unpackRate(packed, out) {
  const bytes = inflateSync(packed);
  const u16 = new Uint16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  const rate = out || new Float32Array(u16.length);
  for (let i = 0; i < u16.length; i++) rate[i] = u16[i] === MISSING_U16 ? NaN : u16[i] / U16_SCALE;
  return rate;
}
